import { Children, forwardRef, HTMLAttributes, ReactElement, useMemo, useRef } from 'react';
import { measureElement, useVirtualizer } from '@tanstack/react-virtual';

const ESTIMATED_GROUP_BLOCK_HEIGHT = 56;
const LISTBOX_MAX_HEIGHT = 320;
const LISTBOX_PADDING = 8;

const VirtualizedListbox = forwardRef<HTMLDivElement, HTMLAttributes<HTMLElement>>((props, ref) => {
  const { children, ...other } = props;
  const scrollRef = useRef<HTMLDivElement>(null);

  const itemData = useMemo(() => Children.toArray(children) as ReactElement[], [children]);

  const rowVirtualizer = useVirtualizer({
    count: itemData.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ESTIMATED_GROUP_BLOCK_HEIGHT,
    overscan: 4,
    measureElement,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();
  const firstVirtualItem = virtualItems[0];
  const lastVirtualItem = virtualItems.at(-1);
  const paddingTop = firstVirtualItem?.start ?? 0;
  const paddingBottom = lastVirtualItem ? Math.max(0, totalSize - lastVirtualItem.end) : 0;

  return (
    <div ref={ref}>
      <div
        {...other}
        ref={scrollRef}
        style={{
          maxHeight: LISTBOX_MAX_HEIGHT,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}>
        <div style={{ paddingTop: LISTBOX_PADDING, paddingBottom: LISTBOX_PADDING }}>
          {paddingTop > 0 && <div style={{ height: paddingTop }} />}
          {virtualItems.map(virtualRow => {
            const child = itemData[virtualRow.index];
            if (!child) {
              return null;
            }

            return (
              <div key={virtualRow.key} data-index={virtualRow.index} ref={rowVirtualizer.measureElement}>
                {child}
              </div>
            );
          })}
          {paddingBottom > 0 && <div style={{ height: paddingBottom }} />}
        </div>
      </div>
    </div>
  );
});

export default VirtualizedListbox;